import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { PasswordChangeModal } from '@/components/auth/PasswordChangeModal';
import { MFAChallenge } from '@/components/auth/MFAChallenge';
import { logger } from '@/utils/logger';
import { queryClient } from '@/lib/queryClient';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [showMfaChallenge, setShowMfaChallenge] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      
      // Boot check: se há sessão persistida mas a última atividade excede
      // um limite máximo absoluto (24h), forçar logout antes de hidratar.
      // O timeout fino por inatividade é tratado em useSessionTimeout.
      if (session?.user) {
        try {
          const raw = localStorage.getItem('arete:last_activity');
          const last = raw ? parseInt(raw, 10) : null;
          const ABSOLUTE_MAX_MS = 24 * 60 * 60 * 1000; // 24h
          if (last && Date.now() - last > ABSOLUTE_MAX_MS) {
            await supabase.auth.signOut();
            localStorage.removeItem('arete:last_activity');
            setSession(null);
            setUser(null);
            setLoading(false);
            return;
          }
        } catch {}
      }

      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        checkPasswordRequirement(session.user.id);
        queryClient.invalidateQueries({ queryKey: ['profiles'] });
      }
    });
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted || event === 'INITIAL_SESSION') return;
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        if (session?.user) {
          checkPasswordRequirement(session.user.id);
          if (event === 'SIGNED_IN') {
            queryClient.invalidateQueries({ queryKey: ['profiles'] });
          }
        } else {
          setMustChangePassword(false);
          setShowPasswordModal(false);
          setShowMfaChallenge(false);
          setMfaFactorId(null);
        }
      }
    );
    
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const checkPasswordRequirement = async (userId: string) => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('must_change_password')
        .eq('id', userId)
        .single();

      if (!error && profile?.must_change_password) {
        setMustChangePassword(true);
        setShowPasswordModal(true);
      } else {
        setMustChangePassword(false);
        setShowPasswordModal(false);
      }
    } catch (error) {
      logger.error('Erro ao verificar mudança de senha');
    }
  };

  const checkMfaFactors = async (): Promise<string | null> => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) return null;
      const verified = (data.totp || []).find(f => f.status === 'verified');
      return verified?.id || null;
    } catch {
      return null;
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (!error && data.session) {
      // Check if user has MFA factors
      const factorId = await checkMfaFactors();
      if (factorId) {
        setMfaFactorId(factorId);
        setShowMfaChallenge(true);
      }
    }
    
    return { error };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { data: selfSignupSetting, error: settingError } = await supabase
      .from('system_settings')
      .select('value')
      .eq('key', 'self_signup')
      .maybeSingle();

    const settings = (!settingError && selfSignupSetting?.value) 
      ? selfSignupSetting.value as any 
      : { enabled: true, admin_name: "Administrador do Sistema", admin_phone: "(00) 0000-0000", admin_location: "Administração Central" };

    if (!settings.enabled) {
      return { 
        error: new Error(`Auto-cadastro desabilitado. Entre em contato com ${settings.admin_name} (${settings.admin_phone}) em ${settings.admin_location} para solicitar acesso.`)
      };
    }

    const { error: requestError } = await supabase
      .from('user_signup_requests')
      .insert([{
        email,
        full_name: fullName,
        role_requested: 'PADRAO'
      }]);

    return { error: requestError };
  };

  const signOut = async () => {
    try { localStorage.removeItem('arete:last_activity'); } catch {}
    await supabase.auth.signOut();
  };

  const handleMfaSuccess = () => {
    setShowMfaChallenge(false);
    setMfaFactorId(null);
  };

  const handleMfaCancel = async () => {
    setShowMfaChallenge(false);
    setMfaFactorId(null);
    await supabase.auth.signOut();
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>
    {showMfaChallenge && mfaFactorId ? (
      <MFAChallenge
        factorId={mfaFactorId}
        onSuccess={handleMfaSuccess}
        onCancel={handleMfaCancel}
      />
    ) : (
      <>
        {children}
        <PasswordChangeModal
          open={showPasswordModal}
          onClose={() => {
            setShowPasswordModal(false);
            setMustChangePassword(false);
          }}
          isFirstLogin={mustChangePassword}
        />
      </>
    )}
  </AuthContext.Provider>;
};
